import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { BADGE_TONE_OPTIONS, prospectStatusBadgeClass } from '../../utils/prospectStatus';
import Button from '../ui/Button';
import Select from '../ui/Select';
import Checkbox from '../ui/Checkbox';
import { useConfirmDialog } from '../ui/ConfirmDialog';

function companyRequestConfig(companyId) {
  return companyId ? { params: { company_id: companyId } } : {};
}

const emptyStatusForm = () => ({
  code: '',
  label: '',
  sort_order: 0,
  badge_tone: 'slate',
  is_default: false,
  is_active: true,
});

function StatusRow({ item, statuses, onEdit, onDelete, t }) {
  const sc = t.settings?.systemConfig || {};

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-secondary-200/70 bg-white/60 px-4 py-3  ">
      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${prospectStatusBadgeClass(item.code, statuses)}`}>
        {item.label}
      </span>
      <span className="text-xs font-mono text-secondary-500 ">{item.code}</span>
      {item.is_default ? (
        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700  ">
          {sc.defaultBadge || 'Default'}
        </span>
      ) : null}
      {!item.is_active ? (
        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-600  ">
          {sc.inactiveBadge || 'Inactive'}
        </span>
      ) : null}
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="rounded-lg p-2 text-secondary-500 hover:bg-secondary-100 hover:text-orange-600  "
          aria-label={sc.edit || 'Edit'}
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(item)}
          className="rounded-lg p-2 text-secondary-500 hover:bg-red-50 hover:text-red-600  "
          aria-label={sc.delete || 'Delete'}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function StatusForm({ form, setForm, onSave, onCancel, saving, t }) {
  const sc = t.settings?.systemConfig || {};

  return (
    <div className="space-y-3 rounded-xl border border-orange-200/60 bg-orange-50/30 p-4  ">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-secondary-700 ">{sc.code || 'Code'}</span>
          <input
            className="input-field"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="NEW"
            disabled={Boolean(form.id)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-secondary-700 ">{sc.label || 'Label'}</span>
          <input
            className="input-field"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder={sc.labelPlaceholder || 'Display name'}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-secondary-700 ">{sc.sortOrder || 'Sort order'}</span>
          <input
            type="number"
            className="input-field"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-secondary-700 ">{sc.badgeTone || 'Badge color'}</span>
          <Select
            className="input-field"
            value={form.badge_tone}
            onChange={(e) => setForm((f) => ({ ...f, badge_tone: e.target.value }))}
          >
            {BADGE_TONE_OPTIONS.map((tone) => (
              <option key={tone} value={tone}>{tone}</option>
            ))}
          </Select>
        </label>
      </div>
      <Checkbox
        id="status-is-default"
        checked={form.is_default}
        onChange={(checked) => setForm((f) => ({ ...f, is_default: checked }))}
        label={sc.setAsDefault || 'Set as default status for new prospects'}
      />
      <Checkbox
        id="status-is-active"
        checked={form.is_active}
        onChange={(checked) => setForm((f) => ({ ...f, is_active: checked }))}
        label={sc.active || 'Active'}
      />
      <div className="flex gap-2">
        <Button size="small" icon={CheckIcon} loading={saving} onClick={onSave}>
          {sc.save || 'Save'}
        </Button>
        <Button size="small" variant="secondary" icon={XMarkIcon} onClick={onCancel}>
          {sc.cancel || 'Cancel'}
        </Button>
      </div>
    </div>
  );
}

function AdminSystemConfig({ presetCompanyId = null }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm, dialog } = useConfirmDialog();
  const sc = t.settings?.systemConfig || {};
  const isPlatformAdmin = user?.is_admin && !user?.company_id;
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusForm, setStatusForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const activeCompanyId = user?.company_id || presetCompanyId || null;

  const fetchData = useCallback(async () => {
    if (isPlatformAdmin && !activeCompanyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(
        '/api/admin/system-config/prospect-statuses',
        { withCredentials: true, ...companyRequestConfig(activeCompanyId) }
      );
      setStatuses(data);
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || sc.failedToLoad || 'Failed to load system configuration';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, isPlatformAdmin, sc.failedToLoad]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function saveStatus() {
    if (!statusForm?.label?.trim()) {
      toast.error(sc.labelRequired || 'Label is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: statusForm.code,
        label: statusForm.label.trim(),
        sort_order: statusForm.sort_order,
        badge_tone: statusForm.badge_tone,
        is_default: statusForm.is_default,
        is_active: statusForm.is_active,
      };
      const requestConfig = { withCredentials: true, ...companyRequestConfig(activeCompanyId) };
      if (statusForm.id) {
        await axios.put(`/api/admin/system-config/prospect-statuses/${statusForm.id}`, payload, requestConfig);
      } else {
        await axios.post('/api/admin/system-config/prospect-statuses', payload, requestConfig);
      }
      toast.success(sc.saved || 'Saved');
      setStatusForm(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || sc.failedToSave || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function deleteStatus(item) {
    if (!(await confirm({
      message: (sc.confirmDeleteStatus || 'Delete status "{name}"?').replace('{name}', item.label),
      title: t.common.confirmDestructiveTitle,
      confirmLabel: t.common.delete,
    }))) return;
    try {
      await axios.delete(
        `/api/admin/system-config/prospect-statuses/${item.id}`,
        { withCredentials: true, ...companyRequestConfig(activeCompanyId) }
      );
      toast.success(sc.deleted || 'Deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || sc.failedToDelete || 'Failed to delete');
    }
  }

  if (loading) {
    return <p className="text-sm text-secondary-600 ">{sc.loading || 'Loading…'}</p>;
  }

  return (
    <div className="space-y-4">
      {dialog}
      <div className="space-y-2">
        {statuses.map((item) => (
          <StatusRow
            key={item.id}
            item={item}
            statuses={statuses}
            t={t}
            onEdit={(row) => setStatusForm({ ...row })}
            onDelete={deleteStatus}
          />
        ))}
      </div>
      {statusForm ? (
        <StatusForm
          form={statusForm}
          setForm={setStatusForm}
          onSave={saveStatus}
          onCancel={() => setStatusForm(null)}
          saving={saving}
          t={t}
        />
      ) : (
        <Button
          size="small"
          variant="secondary"
          icon={PlusIcon}
          onClick={() => setStatusForm(emptyStatusForm())}
        >
          {sc.addStatus || 'Add status'}
        </Button>
      )}
    </div>
  );
}

export default AdminSystemConfig;
